import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { map, switchMap } from 'rxjs';
import { SelectorDialogComponent, SelectorDialogDataRow } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { resolveCompanyProductIdFromParamMap } from '../company-product-detail.component';

@Component({
  selector: 'app-company-product-communication',
  templateUrl: './company-product-communication.component.html',
  styleUrls: ['./company-product-communication.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, CompanyProductPageIdentityComponent],
})
export class CompanyProductCommunicationComponent {
  private readonly activated_route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly employee = inject(EmployeeService);
  private readonly dialog = inject(MatDialog);

  readonly company_product_id = resolveCompanyProductIdFromParamMap(this.activated_route.parent!.snapshot.paramMap);
  readonly refresh_tick = signal(0);
  readonly saving = signal(false);
  readonly loading_channels = signal(false);

  readonly company_product = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api.getCompanyProduct(String(this.company_product_id), {
          with: 'internal_team_messaging_conversation',
        })
      ),
      map((company_product: CompanyProduct): CompanyProduct | null => company_product)
    ),
    { initialValue: null }
  );

  readonly has_configured_channel = computed((): boolean => {
    const company_product = this.company_product();
    return company_product?.internal_team_messaging_conversation_id != null;
  });

  readonly configured_channel_display_name = computed((): string => {
    const conversation = this.company_product()?.internal_team_messaging_conversation;
    if (conversation == null) {
      return '';
    }

    const channel_name = conversation.display_name;
    return channel_name.startsWith('#') ? channel_name.slice(1) : channel_name;
  });

  readonly configured_conversation_id = computed((): string | null => {
    if (!this.has_configured_channel()) {
      return null;
    }

    return this.company_product()?.internal_team_messaging_conversation?.slack_channel_id ?? null;
  });

  readonly status_pill_label = computed((): string => {
    if (this.has_bot_not_member()) {
      return 'Bot no añadido';
    }

    return this.has_configured_channel() ? 'Configurado' : 'Pendiente';
  });

  readonly has_bot_not_member = computed((): boolean => {
    const conversation = this.company_product()?.internal_team_messaging_conversation;
    return conversation != null && conversation.bot_is_member === false;
  });

  readonly channel_selector_button_label = computed((): string => {
    if (this.loading_channels()) {
      return 'Cargando conversaciones…';
    }

    if (this.saving()) {
      return 'Guardando…';
    }

    return this.has_configured_channel() ? 'Cambiar conversación' : 'Seleccionar conversación';
  });

  openChannelSelector(): void {
    if (this.loading_channels() || this.saving()) {
      return;
    }

    this.loading_channels.set(true);
    this.api
      .getBisualCompany()
      .pipe(
        switchMap(bisual_company =>
          this.api.getMessagingConversations({
            company_id: bisual_company.id,
            provider: 'slack',
            per_page: 500,
            scopes: 'active',
          })
        )
      )
      .subscribe({
        next: (response): void => {
          this.loading_channels.set(false);
          this.openSelectorDialog(response.data);
        },
        error: (): void => {
          this.loading_channels.set(false);
        },
      });
  }

  private openSelectorDialog(conversations: MessagingConversation[]): void {
    const company_product = this.company_product();
    const current_conversation_id = company_product?.internal_team_messaging_conversation_id ?? null;

    const rows: SelectorDialogDataRow[] = conversations.map(
      (conversation: MessagingConversation): SelectorDialogDataRow => ({
        value: conversation,
        textToShow: formatMessagingConversationLabel(conversation),
      })
    );

    this.dialog
      .open(SelectorDialogComponent, {
        width: '480px',
        data: {
          dialog_title: 'Seleccionar canal Slack interno Bisual',
          dialog_ok_button: 'Usar esta conversación',
          dialog_cancel_button: 'Cancelar',
          data: rows,
          initial_selected_values:
            current_conversation_id != null ? rows.filter(row => (row.value as MessagingConversation).id === current_conversation_id) : [],
        },
      })
      .afterClosed()
      .subscribe((selected: MessagingConversation | undefined): void => {
        if (selected == null) {
          return;
        }

        this.saveSelectedConversation(selected);
      });
  }

  private saveSelectedConversation(conversation: MessagingConversation): void {
    this.saving.set(true);
    this.api
      .updateCompanyProductBisualInternalTeamSlackChannel(this.company_product_id, {
        internal_team_messaging_conversation_id: conversation.id,
      })
      .subscribe({
        next: (): void => {
          this.saving.set(false);
          this.employee.refreshActionsRequired();
          this.refresh_tick.update((tick: number): number => tick + 1);
        },
        error: (): void => {
          this.saving.set(false);
        },
      });
  }
}

function formatMessagingConversationLabel(conversation: MessagingConversation): string {
  const name = conversation.display_name.startsWith('#') ? conversation.display_name : `#${conversation.display_name}`;
  const slack_id = conversation.slack_channel_id ?? '';

  return slack_id !== '' ? `${name} (${slack_id})` : name;
}
